//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ViewModuleIcon from '@mui/icons-material/ViewModule'

import { Box, Button, Paper, Typography, useTheme } from '@mui/material'

import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid-premium'
import React, { useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import ButtonModal from '@react/components/ButtonModal'
import { deleteCompanyProfile, fetchCompanyProfiles } from '@react/fetchers/companyProfileFetchers'
import { poolActions } from '@react/redux/poolSlice'
import { OtStoreState, useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { ReportDescriptor, ReportFetchParams } from '@react/reporting/ReportDescriptors'
import { ReportRowGeneratorClass } from '@react/reporting/ReportRowGeneratorClass'
import { CompanyProfileCTOSchema } from '@shared/CompanyProfileCTO'
import { ReportCategory, ReportDataShape } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { JsonRpcProvider, ZeroAddress } from 'ethers'
import CompanyProfileForm from './CompanyProfileForm'
import { ReportExplorer } from '@react/reporting/ReportExplorer'

export default function AdminCompanyProfileList(): React.ReactElement {
  const {
    chainConfiguration: { providerReadOnly },
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
    cognitoUser,
  } = useOtChainState()
  const [, forceRerender] = useState({})
  const [reportRowGeneratorClass, setReportRowGeneratorClass] = useState<ReportRowGeneratorClass | undefined>(undefined)
  const companyProfiles = useOtSelector((state: OtStoreState) => state.poolState.companyProfiles)

  const navigate = useNavigate()
  const theme = useTheme()
  const dispatch = useOtDispatch()

  const REPORT_FETCH_DESCRIPTOR_COMPANY_PROFILES: ReportDescriptor = {
    label: 'Transaction Ledger',
    key: 'COMPANY_PROFILES',
    category: ReportCategory.COMPANY_PROFILES,

    selectors: {},
    poolTypeFilter: undefined,
    dataShape: ReportDataShape.COMPANY_PROFILES,
    schema: CompanyProfileCTOSchema,

    fetcher: async (_params: ReportFetchParams) => {
      if (cognitoUser) {
        return fetchCompanyProfiles(serverApiBaseUrl, restAPIKey, cognitoUser, true)
          .then((_companyProfiles) => {
            logOT.info('refreshCallBack companyProfiles', _companyProfiles)
            const _companyProfilesMap = _companyProfiles.map((cp) => {
              return {
                ...cp,
                id: cp.uuid,
              }
            })
            dispatch(poolActions.setCompanyProfiles(_companyProfilesMap))
            forceRerender({})
            return _companyProfilesMap
          })
          .catch((error) => {
            logOT.error('refreshCallBack companyProfiles error', error)
            return []
          })
      } else {
        logOT.info('refreshCallBack companyProfiles empty', [])
        return []
      }
    },
    actionColumns: (params: GridRowParams<any>) => {
      return [
        <GridActionsCellItem
          key="view"
          icon={<ViewModuleIcon />}
          onClick={() => {
            navigate(`/companyProfiles/${params.row.uuid}`)
          }}
          label="Edit"
        />,
        <ButtonModal icon={<EditIcon />} key="edit" title={'Edit'} variant="primary">
          <CompanyProfileForm companyProfileCTO={params.row} companyProfiles={companyProfiles} />
        </ButtonModal>,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          onClick={() => {
            if (cognitoUser) {
              deleteCompanyProfile(serverApiBaseUrl, restAPIKey, cognitoUser, params.row.uuid)
                .then(() => {
                  return fetchCompanyProfiles(serverApiBaseUrl, restAPIKey, cognitoUser, true)
                })
                .then((companyProfiles) => {
                  dispatch(poolActions.setCompanyProfiles(companyProfiles))
                })
                .catch((error) => {
                  logOT.error('deleteCompanyProfile error', error)
                })
            }
          }}
          label="Delete"
        />,
      ]
    },
    logoProp: 'uuid',
  }

  useEffect(() => {
    window.addEventListener(
      'modalClose',
      () => {
        reportRowGeneratorClass?.refreshData()
      },
      false
    )

    return () => {
      window.removeEventListener(
        'modalClose',
        () => {
          reportRowGeneratorClass?.refreshData()
        },
        false
      )
    }
  })

  useEffect(() => {
    const _reportRowGeneratorClass = new ReportRowGeneratorClass({
      reportDescriptor: REPORT_FETCH_DESCRIPTOR_COMPANY_PROFILES,
      providerReadOnly: providerReadOnly as JsonRpcProvider,
      serverApiBaseUrl: serverApiBaseUrl,
      restAPIKey: restAPIKey,
      poolAddr: ZeroAddress,
      lenderAddr: ZeroAddress,
    })
    setReportRowGeneratorClass(_reportRowGeneratorClass)
    _reportRowGeneratorClass.refreshData()
  }, [])

  logOT.debug('companyProfiles ', companyProfiles)

  if (companyProfiles.length === 0) {
    return (
      <>
        <Typography align="left" sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Company Profiles
        </Typography>
        <ButtonModal title={'Add Company Profile'} variant="primary">
          <CompanyProfileForm companyProfiles={companyProfiles} />
        </ButtonModal>

        <Typography variant="h4" gutterBottom component="div">
          No Company Profiles
        </Typography>
      </>
    )
  }
  return (
    <Paper
      data-testid="AdminCompanyProfileList"
      sx={{
        marginTop: '20px',
        marginBottom: 4,
        marginRight: '2rem',
        marginLeft: '2rem',
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <Typography
          align="left"
          sx={{
            fontWeight: 600,
            fontSize: '2rem',
            color: '#171717',
          }}
          variant={'h4'}
        >
          Company Profiles
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ButtonModal
            title={'Add Company Profile'}
            variant="primary"
            sx={{
              backgroundColor: '#171717',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#333333',
              },
            }}
          >
            <CompanyProfileForm companyProfiles={companyProfiles} />
          </ButtonModal>
        </Box>
      </Box>
      <Box sx={{ marginBottom: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row' }}>
          {cognitoUser && (
            <Button
              variant="contained"
              sx={{
                backgroundColor: theme.palette.fg.main,
                color: theme.palette.fg.fg,
                padding: '8px 10px 8px 10px',
                borderRadius: '6px',
              }}
              onClick={() => {
                reportRowGeneratorClass?.refreshData().then(() => {
                  forceRerender({})
                })
              }}
            >
              Refresh
            </Button>
          )}
        </Box>
      </Box>
      {reportRowGeneratorClass && <ReportExplorer reportRowGeneratorClass={reportRowGeneratorClass} />}
    </Paper>
  )
}
